import { useAppDispatch, useAppSelector } from '@store/hooks';
import { initAppInsights } from './monitoring/appInsights';

import { createElement, FC, Suspense, useEffect, useMemo } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import useAuth from './hooks/useAuth';
import { useResetReportingFiltersOnExit } from '@hooks/useResetReportingFiltersOnExit';
import './index.css';
import './style.scss';
import { SendMessageRides } from '@component/SendMessageRides/SendMessageRides';
import { selectUsers } from '@store/slices/users/usersSlice';
import { LoaderComponent } from '@component/LoaderComponent/LoaderComponent';
import { setCurrentPath } from '@store/slices/global/global';
import NoAutocompleteProvider from './components/NoAutocompleteProvider/NoAutocompleteProvider';
import { LyftProvider } from '@provider/LyftProvider';
import { AlertProvider } from '@provider/AlertProvider';
import AuthGuard from '@component/AutGuard/AuthGuard';
import CountdownDisplay from './components/CountdownDisplay/CountdownDisplay';
import { useSessionHeaderSync } from '@hooks/useSessionHeaderSync';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { GOOGLE_CLIENT_ID } from '@utils/constants';
import { AppleCallback } from '@component/AppleCallback/AppleCallback';
import { MicrosoftCallback } from '@component/MicrosoftCallback/MicrosoftCallback';
import {
  MICROSOFT_TOGGLE_STORAGE_KEY,
  PERMISSIONS_TOGGLE_STORAGE_KEY,
} from '@uikit/Tables/TableDynamic/TableDynamic';
import { setDefaultStorageItems } from '@utils/helpers';
import { DRIVER_MESSAGE_TOGGLE_STORAGE_KEY } from '@component/DriverChat/constants';
import { resolveRole } from '@routes/resolveRole';
import { resolveRoutesForRole } from '@routes/routesByRole';
import { publicRoutes } from '@routes/publicRoutes';
import { RouteConfig } from '@routes/types';

initAppInsights();

// TODO this is temporary // for testing only
setDefaultStorageItems(PERMISSIONS_TOGGLE_STORAGE_KEY);
setDefaultStorageItems(MICROSOFT_TOGGLE_STORAGE_KEY);
setDefaultStorageItems(DRIVER_MESSAGE_TOGGLE_STORAGE_KEY, true);

const App: FC = () => {
  const dispatch = useAppDispatch();
  const { userInfo, isContractorVersion, userType } =
    useAppSelector(selectUsers);

  const location = useLocation();

  useAuth();
  useSessionHeaderSync();
  useResetReportingFiltersOnExit();

  useEffect(() => {
    dispatch(setCurrentPath());
  }, [location]);

  const isTrackPage = location.pathname.startsWith('/track');

  const role = useMemo(
    () => resolveRole({ userInfo, isContractorVersion }),
    [userInfo, isContractorVersion]
  );

  const activeRoutes: RouteConfig[] = role
    ? resolveRoutesForRole(userType, { userInfo, isContractorVersion })
    : [];

  return (
    <NoAutocompleteProvider>
      <div
        className="wrapper"
        style={isTrackPage ? { backgroundColor: '#57568e' } : {}}
      >
        <LyftProvider>
          <AlertProvider>
            <ToastContainer autoClose={4000} style={{ zIndex: 2147483647 }} />
            <CountdownDisplay userInfo={userInfo} />
            <Suspense fallback={<LoaderComponent />}>
              <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
                <Routes>
                  <Route path={'track/:id'} element={<SendMessageRides />} />
                  <Route path={'apple/callback'} element={<AppleCallback />} />
                  <Route
                    path={'microsoft/callback'}
                    element={<MicrosoftCallback />}
                  />
                  {renderRoutes(publicRoutes)}
                  <Route
                    path="*"
                    element={
                      <AuthGuard routes={activeRoutes}>
                        <Routes>{renderRoutes(activeRoutes)}</Routes>
                      </AuthGuard>
                    }
                  />
                </Routes>
              </GoogleOAuthProvider>
            </Suspense>
          </AlertProvider>
        </LyftProvider>
      </div>
    </NoAutocompleteProvider>
  );
};

const renderRoutes = (routes: RouteConfig[]) => {
  return routes.map((route, index) => {
    const { path, element, children } = route;

    return (
      <Route key={index} path={path} element={createElement(element)}>
        {children && renderRoutes(children)}
      </Route>
    );
  });
};

export default App;
